import { useEffect, useMemo, useRef } from "react";
import type { Availability } from "../../../common/jsonRpcTypes";
import { blocksProgressToDetails, isEnquiryOutcome } from "../../utils/availabilityOutcome";
import type { CoreBookingDetails } from "./useBookingCreate";

/**
 * Minimal subset of parsed URL query parameters needed for seeding.
 * Avoids coupling the hook to the full ParsedBookingQueryParams type.
 */
interface SeedableQueryParams {
  type?: string;
  date?: string;
  time?: string;
  duration?: number | null;
  num_people?: number;
}

interface UseUrlParamSeedingParams {
  /** URL query parameters to seed into booking state. */
  queryParams: SeedableQueryParams;
  /** Whether venue config has loaded (seeding waits for this). */
  venueConfigLoaded: boolean;
  /** Current loading state from the booking hook. */
  loading: boolean;
  /** Error string from createBooking. */
  error: string | null;
  /** Validation errors map from createBooking. */
  errors: Record<string, string[]> | null;
  /** Current booking details from the state (to avoid duplicate API calls). */
  currentBookingDetails: CoreBookingDetails;
  /** Latest availability outcome for the seeded details, used to decide whether the guest can be skipped ahead. */
  availability: Availability | null;
  /** Call updateBooking with the seeded values (triggers API call + availability check). */
  updateBooking: (details: Partial<CoreBookingDetails>) => Promise<void>;
  /** Call updateCoreDetails to pre-fill form fields without triggering API. */
  updateCoreDetails: (details: Partial<CoreBookingDetails>) => void;
  /** Navigate to the given stage when auto-skip succeeds. */
  navigateToStage: (stage: "details", isEnquiry?: boolean) => void;
}

/**
 * Check whether all required booking details are present in the URL query
 * parameters. When every field is provided the initial "details" stage can
 * be auto-submitted and skipped.
 */
export function hasAllRequiredParams(params: SeedableQueryParams): boolean {
  return !!(
    params.num_people &&
    params.date &&
    params.time &&
    params.duration !== undefined &&
    params.duration !== null &&
    params.duration > 0 &&
    params.type
  );
}

/**
 * Build a CoreBookingDetails update object from the URL query parameters.
 * Returns `null` if no seedable parameters are present.
 */
export function buildSeedUpdates(params: SeedableQueryParams): Partial<CoreBookingDetails> | null {
  const updates: Partial<CoreBookingDetails> = {};
  if (params.type) updates.type = params.type;
  if (params.date) updates.date = params.date;
  if (params.time) updates.time = params.time;
  if (params.duration !== undefined) updates.duration = params.duration;
  if (params.num_people) updates.num_people = params.num_people;
  return Object.keys(updates).length > 0 ? updates : null;
}

/**
 * Check if the current booking details already match the URL parameters.
 * Used to avoid duplicate API calls when the initial load has already
 * populated the state with the same values.
 *
 * Treats undefined params as matching null values in current details,
 * since undefined means the parameter was not provided in the URL.
 */
export function bookingDetailsMatchParams(current: CoreBookingDetails, params: SeedableQueryParams): boolean {
  return (
    (params.type === undefined ? current.type === null : current.type === params.type) &&
    (params.date === undefined ? current.date === null : current.date === params.date) &&
    (params.time === undefined ? current.time === null : current.time === params.time) &&
    (params.duration === undefined ? current.duration === null : current.duration === params.duration) &&
    (params.num_people === undefined ? current.num_people === null : current.num_people === params.num_people)
  );
}

/**
 * Skip the guest past the booking details form for a seeded URL, unless the
 * availability outcome is one they have to resolve themselves.
 *
 * A rejected slot, or one that came back with alternative times/venues, keeps
 * the guest on the booking details form so its availability feedback can be
 * shown — the same gate DMN applies around its customer details block. Any
 * other non-accepted outcome carries them forward as an enquiry.
 */
function skipToDetails(
  availability: Availability | null,
  navigateToStage: (stage: "details", isEnquiry?: boolean) => void,
): void {
  if (blocksProgressToDetails(availability)) {
    return;
  }

  const isEnquiry = isEnquiryOutcome(availability);
  navigateToStage("details", isEnquiry);
}

/**
 * useUrlParamSeeding — seeds booking form fields from URL parameters.
 *
 * When all required params are present, triggers an `updateBooking` call and
 * auto-navigates to the "details" stage once the API responds without errors.
 * When only partial params are present, pre-fills the form via `updateCoreDetails`.
 *
 * Extracted from BookingForm to keep the component's cognitive complexity low.
 */
export function useUrlParamSeeding({
  queryParams,
  venueConfigLoaded,
  loading,
  error,
  errors,
  currentBookingDetails,
  availability,
  updateBooking,
  updateCoreDetails,
  navigateToStage,
}: UseUrlParamSeedingParams): void {
  const canAutoSkipInitial = useMemo(() => hasAllRequiredParams(queryParams), [queryParams]);

  const hasSeededRef = useRef(false);
  const autoSkipPendingRef = useRef(false);
  const prevLoadingRef = useRef(loading);

  // Seed URL params into booking state once venue config is available.
  useEffect(() => {
    if (hasSeededRef.current || !venueConfigLoaded) return;

    const updates = buildSeedUpdates(queryParams);
    if (!updates) return;

    hasSeededRef.current = true;

    if (canAutoSkipInitial) {
      // Check if the current booking details already match the URL params to avoid duplicate API calls.
      // The initial load in useBookingCreate already calls /createBooking with these params.
      if (bookingDetailsMatchParams(currentBookingDetails, queryParams)) {
        // Still navigate to details stage if there are no errors
        if (!error && (!errors || Object.keys(errors).length === 0)) {
          skipToDetails(availability, navigateToStage);
        }
        return;
      }

      // All params present — call updateBooking and wait for the API
      // response before deciding whether to skip. If the API returns
      // errors (date in past, wrong covers, etc.) we stay on the
      // initial stage so the user can see and fix them.
      autoSkipPendingRef.current = true;
      void updateBooking(updates);
      // Reset the loading tracker so the resolution effect does not
      // mistake the initial createBooking's true→false transition for
      // the updateBooking completion.
      prevLoadingRef.current = false;
    } else {
      // Partial params — seed them so the initial form is pre-filled.
      updateCoreDetails(updates);
    }
  }, [
    venueConfigLoaded,
    queryParams,
    updateCoreDetails,
    canAutoSkipInitial,
    updateBooking,
    currentBookingDetails,
    error,
    errors,
    availability,
    navigateToStage,
  ]);

  // Auto-skip resolution: once the updateBooking call finishes (loading
  // transitions from true → false), navigate to "details" only when there are
  // no validation errors and the availability outcome does not need the guest
  // to choose (see skipToDetails).
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (!autoSkipPendingRef.current) return;
    // Only act on a true → false transition (loading cycle completed).
    if (!wasLoading || loading) return;
    autoSkipPendingRef.current = false;

    const hasErrors = !!error || (errors && Object.keys(errors).length > 0);

    if (!hasErrors) {
      skipToDetails(availability, navigateToStage);
    }
  }, [loading, error, errors, availability, navigateToStage]);
}
